import { businessTripsUrl } from 'router/url';
import { RoleTypes } from 'utils/rolesTypes';
import { TripStatuses } from './types';

export type TripScope = 'all' | 'team' | 'mine';
export type TripPreset = 'needs' | 'open' | 'all' | 'custom';

export interface TripAccess {
  isLeader: boolean;
  isAccounting: boolean;
  isAdministration: boolean;
  isDevHr: boolean;
}

export interface TripFilterState {
  scope: TripScope;
  preset: TripPreset;
  statuses: TripStatuses[];
}

export const STATUS_GROUPS: { label: string; statuses: TripStatuses[] }[] = [
  {
    label: 'BUSINESS_TRIP.STATUS_GROUP.PLANNING',
    statuses: [
      TripStatuses.DRAFT,
      TripStatuses.TO_BE_APPROVED_BY_LEADER,
      TripStatuses.REJECTED_BY_LEADER,
    ],
  },
  {
    label: 'BUSINESS_TRIP.STATUS_GROUP.BOOKING',
    statuses: [
      TripStatuses.APPROVED,
      TripStatuses.RESERVATION_TO_BE_DONE,
      TripStatuses.RESERVATION_DONE,
    ],
  },
  {
    label: 'BUSINESS_TRIP.STATUS_GROUP.SETTLEMENT',
    statuses: [
      TripStatuses.FINISHED,
      TripStatuses.DOCS_TO_BE_VERIFIED,
      TripStatuses.DOCS_TO_UPDATE,
      TripStatuses.TO_BE_FINALLY_APPROVED_BY_LEADER,
    ],
  },
  {
    label: 'BUSINESS_TRIP.STATUS_GROUP.CLOSED',
    statuses: [TripStatuses.SETTLED, TripStatuses.CANCELED],
  },
];

export const FILTERABLE_STATUSES = STATUS_GROUPS.flatMap(
  ({ statuses }) => statuses,
);

export const OPEN_STATUSES = FILTERABLE_STATUSES.filter(
  (status) =>
    status !== TripStatuses.SETTLED && status !== TripStatuses.CANCELED,
);

const PRESETS: TripPreset[] = ['needs', 'open', 'all', 'custom'];

export const getTripAccess = (
  roles: string[],
  isLeader: boolean,
): TripAccess => ({
  isLeader,
  isAccounting: roles.includes(RoleTypes.RoleAccounting),
  isAdministration: roles.includes(RoleTypes.RoleAdministration),
  isDevHr: roles.includes(RoleTypes.RoleDEVHR),
});

const isPrivileged = (access: TripAccess) =>
  access.isAccounting || access.isAdministration || access.isDevHr;

export const getAvailableScopes = (access: TripAccess): TripScope[] => [
  ...(isPrivileged(access) ? (['all'] as TripScope[]) : []),
  ...(access.isLeader ? (['team'] as TripScope[]) : []),
  'mine',
];

export const getDefaultTripFilters = (access: TripAccess): TripFilterState => {
  const scope = getAvailableScopes(access)[0];
  return {
    scope,
    preset: scope === 'mine' ? 'open' : 'needs',
    statuses: [],
  };
};

export const getNeedsActionStatuses = (
  scope: TripScope,
  access: TripAccess,
): TripStatuses[] => {
  if (scope === 'mine') {
    return [
      TripStatuses.DRAFT,
      TripStatuses.REJECTED_BY_LEADER,
      TripStatuses.FINISHED,
      TripStatuses.DOCS_TO_UPDATE,
    ];
  }
  if (scope === 'team') {
    return [
      TripStatuses.TO_BE_APPROVED_BY_LEADER,
      TripStatuses.TO_BE_FINALLY_APPROVED_BY_LEADER,
    ];
  }
  return [
    ...(access.isAdministration || access.isDevHr
      ? [TripStatuses.RESERVATION_TO_BE_DONE]
      : []),
    ...(access.isAccounting || access.isDevHr
      ? [TripStatuses.DOCS_TO_BE_VERIFIED]
      : []),
  ];
};

export const getEffectiveStatuses = (
  { scope, preset, statuses }: TripFilterState,
  access: TripAccess,
): TripStatuses[] => {
  if (preset === 'needs') {
    return getNeedsActionStatuses(scope, access);
  }
  if (preset === 'open') {
    return OPEN_STATUSES;
  }
  if (preset === 'all') {
    return [];
  }
  return statuses;
};

const sameStatuses = (a: TripStatuses[], b: TripStatuses[]) =>
  a.length === b.length && a.every((status) => b.includes(status));

export const selectStatuses = (
  selected: TripStatuses[],
  scope: TripScope,
  access: TripAccess,
): Pick<TripFilterState, 'preset' | 'statuses'> => {
  if (sameStatuses(selected, getNeedsActionStatuses(scope, access))) {
    return { preset: 'needs', statuses: [] };
  }
  if (sameStatuses(selected, OPEN_STATUSES)) {
    return { preset: 'open', statuses: [] };
  }
  if (!selected.length || sameStatuses(selected, FILTERABLE_STATUSES)) {
    return { preset: 'all', statuses: [] };
  }
  return { preset: 'custom', statuses: selected };
};

// A custom selection that matches a preset in its scope becomes that preset,
// so a filter state looks the same before and after a reload of its URL.
export const normalizeTripFilters = (
  { scope, preset, statuses }: TripFilterState,
  access: TripAccess,
): TripFilterState =>
  preset === 'custom'
    ? { scope, ...selectStatuses(statuses, scope, access) }
    : { scope, preset, statuses: [] };

export const readTripFiltersFromQuery = (
  query: URLSearchParams,
  defaults: TripFilterState,
  access: TripAccess,
): TripFilterState => {
  const scopeParam = query.get('scope') as TripScope;
  const scope = getAvailableScopes(access).includes(scopeParam)
    ? scopeParam
    : defaults.scope;
  const presetParam = query.get('show') as TripPreset;
  const preset = PRESETS.includes(presetParam) ? presetParam : defaults.preset;
  const statuses = (query.get('statuses') ?? '')
    .split(',')
    .filter((status): status is TripStatuses =>
      FILTERABLE_STATUSES.includes(status as TripStatuses),
    );
  return normalizeTripFilters({ scope, preset, statuses }, access);
};

export const writeTripFiltersToQuery = (
  query: URLSearchParams,
  filters: TripFilterState,
  defaults: TripFilterState,
) => {
  ['scope', 'show', 'statuses'].forEach((key) => query.delete(key));
  if (filters.scope !== defaults.scope) {
    query.set('scope', filters.scope);
  }
  if (filters.preset !== defaults.preset) {
    query.set('show', filters.preset);
  }
  if (filters.preset === 'custom') {
    query.set('statuses', filters.statuses.join(','));
  }
  return query;
};

export interface TripsRequest {
  pageNumber: number;
  pageSize: number;
  scope: TripScope;
  statuses: TripStatuses[];
  username?: string;
  employeeId?: number;
  clientId?: number;
  projectId?: number;
  businessTripId?: string;
}

const getLinkedTripUrl = (
  { pageSize, username, businessTripId }: TripsRequest,
  access: TripAccess,
) => {
  // A linked trip ignores the page filters and pagination and uses the widest
  // endpoint the user may read; for a leader that includes their own trips.
  const searchParams = new URLSearchParams({
    page: '0',
    size: String(pageSize),
    businessTripId: businessTripId ?? '',
  });
  const endpoint = {
    all: businessTripsUrl,
    team: `${businessTripsUrl}/leader`,
    mine: `${businessTripsUrl}/users/${username}`,
  }[getAvailableScopes(access)[0]];
  return `${endpoint}?${searchParams.toString()}`;
};

export const getTripsUrl = (request: TripsRequest, access: TripAccess) => {
  const {
    pageNumber,
    pageSize,
    scope,
    statuses,
    username,
    employeeId,
    clientId,
    projectId,
    businessTripId,
  } = request;
  if (businessTripId) {
    return getLinkedTripUrl(request, access);
  }

  const searchParams = new URLSearchParams({
    page: String(pageNumber),
    size: String(pageSize),
    clientId: clientId ? String(clientId) : '',
    projectId: projectId ? String(projectId) : '',
    businessTripId: '',
  });
  if (statuses.length) {
    searchParams.append('statuses', statuses.join(','));
  }

  if (scope === 'mine') {
    return `${businessTripsUrl}/users/${username}?${searchParams.toString()}`;
  }

  searchParams.append('accountId', employeeId ? String(employeeId) : '');
  if (scope === 'team') {
    searchParams.append('excludeOwn', 'true');
    return `${businessTripsUrl}/leader?${searchParams.toString()}`;
  }
  return `${businessTripsUrl}?${searchParams.toString()}`;
};
